import { getServiceName } from './constants';
import type { TrackContext, TrackEventInput, AnalyticsEventName } from './events';
import { getBrowserPostHog } from './posthog-client';

function buildProperties(
  properties: Record<string, unknown>,
  context?: Partial<TrackContext>,
): Record<string, unknown> {
  const logContext = typeof window === 'undefined' ? {} : {};
  return {
    ...properties,
    actor: context?.actor ?? properties.actor ?? 'client',
    surface: context?.surface ?? properties.surface ?? 'client_flow',
    service: getServiceName(),
    env: process.env.NODE_ENV ?? 'production',
    ...(context?.opportunityId ? { opportunityId: context.opportunityId } : {}),
    ...(context?.accountId ? { accountId: context.accountId } : {}),
    ...(context?.repId ? { repId: context.repId } : {}),
    ...logContext,
  };
}

/** Best-effort client-side PostHog event capture. Never throws. */
export function trackEvent<E extends AnalyticsEventName>(
  input: TrackEventInput<E>,
): void {
  const posthog = getBrowserPostHog();
  if (!posthog) return;

  try {
    posthog.capture(
      input.event,
      buildProperties(input.properties as Record<string, unknown>, input.context),
    );
  } catch {
    /* noop */
  }
}

/** Best-effort client-side exception capture with enriched context. Never throws. */
export function trackException(
  error: Error,
  context?: Partial<TrackContext> & Record<string, unknown>,
): void {
  const posthog = getBrowserPostHog();
  if (!posthog) return;

  try {
    if (posthog.captureException) {
      posthog.captureException(error, buildProperties(context ?? {}, context));
      return;
    }
    posthog.capture('$exception', {
      ...buildProperties(context ?? {}, context),
      $exception_message: error.message,
      $exception_type: error.name,
      $exception_stack_trace_raw: error.stack,
    });
  } catch {
    /* noop */
  }
}

/** Back-compat shim — routes through typed trackEvent. */
export function captureClientPosthogEvent(
  eventName: string,
  properties: Record<string, unknown>,
): void {
  const posthog = getBrowserPostHog();
  if (!posthog) return;

  try {
    posthog.capture(eventName, {
      ...properties,
      service: getServiceName(),
      env: process.env.NODE_ENV ?? 'production',
    });
  } catch {
    /* noop */
  }
}
